import * as THREE from 'three';

/**
 * Pièces de route générées par calcul (arc de cercle exact) plutôt que chargées
 * depuis un `.glb` Kenney — voir la session de debug qui a établi que les
 * modèles Kenney "Large"/"Larger" ne touchent JAMAIS le bord de case (0% de
 * couverture mesurée pixel par pixel, quelle que soit la rotation testée) : le
 * rayon de leur courbe ne correspond à aucun nombre entier de cases. Ici le
 * rayon est calculé pour être exactement tangent aux deux bras, donc le
 * raccord avec la case suivante est garanti par construction, pas mesuré après coup.
 */

const PROCEDURAL_CORNER_PREFIX = 'proceduralCornerArm';

/** Largeur de chaussée / bordure partagées entre virages et lignes droites
 * procéduraux — même valeurs des deux côtés pour un raccord sans décalage
 * radial à la jonction virage/ligne droite. */
export const PROCEDURAL_ROAD_WIDTH = 0.78;
export const PROCEDURAL_CURB_WIDTH = 0.1;
export const PROCEDURAL_ASPHALT_COLOR = 0x6e6e6e;

function hexToCss(hex: number): string {
  return `#${hex.toString(16).padStart(6, '0')}`;
}

/**
 * Dégradé vertical (le long de la longueur de la case) pour fondre la couleur de
 * la chaussée vers celle de la case voisine à une jonction terre/bitume — sans
 * ça, une coupure nette au milieu d'une ligne droite continue trahissait
 * immédiatement le découpage case par case (repéré en testant, voir
 * `circuit3dEditor.ts`). `null` pour une extrémité qui ne fond pas (case sans
 * voisin de revêtement différent de ce côté) : le dégradé s'y aplatit sur
 * `baseColor`, équivalent visuellement à un remplissage uni.
 */
function createSurfaceFadeTexture(
  baseColor: number,
  fadeToStart: number | null,
  fadeToEnd: number | null
): THREE.CanvasTexture {
  const width = 8;
  const height = 128;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  // Volontairement court (18% à chaque bout) : une case isolée entre deux
  // revêtements différents fond des DEUX côtés (voir l'appelant), donc une
  // fraction trop généreuse (0.4 testé) ne laissait quasiment plus de couleur
  // pleine au milieu — juste une tache floue plutôt qu'une vraie case terre,
  // repéré en testant sur une case terre isolée entre deux bitumées.
  const fadeFraction = 0.18;
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, hexToCss(fadeToStart ?? baseColor));
  gradient.addColorStop(fadeFraction, hexToCss(baseColor));
  gradient.addColorStop(1 - fadeFraction, hexToCss(baseColor));
  gradient.addColorStop(1, hexToCss(fadeToEnd ?? baseColor));
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/** Identifiant synthétique (pas un fichier disque) référencé par `CircuitRoadPiece.modelId`. */
export function proceduralCornerSlug(armCells: number): string {
  return `${PROCEDURAL_CORNER_PREFIX}${armCells}`;
}

export function isProceduralCornerSlug(slug: string): boolean {
  return slug.startsWith(PROCEDURAL_CORNER_PREFIX);
}

/** `null` si `slug` n'est pas une pièce procédurale reconnue. */
export function proceduralCornerArmFromSlug(slug: string): number | null {
  if (!isProceduralCornerSlug(slug)) return null;
  const arm = Number(slug.slice(PROCEDURAL_CORNER_PREFIX.length));
  return Number.isInteger(arm) && arm >= 0 ? arm : null;
}

/**
 * Motif de bordure vibreur (rayures diagonales rouge/blanc), avec un fondu vers
 * le blanc uni aux DEUX extrémités de l'arc — sans ça, les rayures s'arrêtaient
 * net en plein milieu d'un motif à la jonction avec la ligne droite/case départ
 * suivante (ou avec le virage suivant sur une chicane serrée), une coupure
 * visuelle franche. Dessiné directement sur toute la largeur (pas de tuilage
 * via `texture.repeat`) : le fondu ne doit apparaître qu'aux deux vraies
 * extrémités de la pièce, pas à chaque répétition du motif.
 */
function createCurbStripeTexture(repeat: number): THREE.CanvasTexture {
  const unit = 64;
  const height = 64;
  const width = Math.max(unit, Math.round(unit * repeat));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#f2f2f2';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = '#c81e1e';
  const stripeWidth = unit / 4;
  for (let x = -height; x < width + height; x += stripeWidth * 2) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x + stripeWidth, 0);
    ctx.lineTo(x + stripeWidth - height, height);
    ctx.lineTo(x - height, height);
    ctx.closePath();
    ctx.fill();
  }
  const fadeWidth = Math.min(unit, width * 0.4);
  const fadeIn = ctx.createLinearGradient(0, 0, fadeWidth, 0);
  fadeIn.addColorStop(0, 'rgba(242,242,242,1)');
  fadeIn.addColorStop(1, 'rgba(242,242,242,0)');
  ctx.fillStyle = fadeIn;
  ctx.fillRect(0, 0, fadeWidth, height);
  const fadeOut = ctx.createLinearGradient(width - fadeWidth, 0, width, 0);
  fadeOut.addColorStop(0, 'rgba(242,242,242,0)');
  fadeOut.addColorStop(1, 'rgba(242,242,242,1)');
  ctx.fillStyle = fadeOut;
  ctx.fillRect(width - fadeWidth, 0, fadeWidth, height);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

export interface ProceduralCornerOptions {
  /** Largeur de la chaussée carrossable (fraction d'une case). */
  roadWidth?: number;
  /** Largeur de la bordure vibreur de chaque côté de la chaussée. */
  curbWidth?: number;
  segments?: number;
  /** Couleur de la chaussée — bitume par défaut, terre pour une case peinte. */
  baseColor?: number;
}

/**
 * Virage arc-de-cercle exact reliant le bras OUEST et le bras SUD du pivot,
 * sur `armCells` cases de chaque côté (0 = juste la case du virage elle-même,
 * comme l'ancien virage par défaut Kenney). Orientation "native" choisie
 * (Sud+Ouest) pour que la SEULE rotation de `tileGroup` (déterminée par
 * `rotationSteps`, voir `track3dLayout.ts`) retombe exactement sur les bras
 * réels, sans rotation supplémentaire à poser dans `circuit3dTrackScene.ts`
 * (vérifié par le calcul pour les 4 valeurs de `rotationSteps`, et par mesure
 * pixel par pixel en session — contrairement aux modèles Kenney
 * "Large"/"Larger", dont l'orientation native différente, est+nord,
 * nécessitait un correctif de rotation propre à eux).
 * Le rayon (`armCells + 0.5`) est tangent par construction aux deux bras :
 * pas de mesure après coup, le raccord est garanti par le calcul.
 */
export function buildProceduralCorner(
  armCells: number,
  opts: ProceduralCornerOptions = {}
): THREE.Object3D {
  const {
    roadWidth = PROCEDURAL_ROAD_WIDTH,
    curbWidth = PROCEDURAL_CURB_WIDTH,
    segments = 48,
    baseColor = PROCEDURAL_ASPHALT_COLOR,
  } = opts;
  const radius = armCells + 0.5;
  const halfRoad = roadWidth / 2;
  const arcLength = radius * (Math.PI / 2);

  const asphaltMat = new THREE.MeshStandardMaterial({
    color: baseColor,
    roughness: 0.95,
  });
  const asphalt = new THREE.Mesh(
    new THREE.RingGeometry(radius - halfRoad, radius + halfRoad, segments, 1, 0, Math.PI / 2),
    asphaltMat
  );

  const curbRepeat = Math.max(1, Math.round(arcLength / (curbWidth * 3)));
  const curbTexture = createCurbStripeTexture(curbRepeat);
  const curbInner = new THREE.Mesh(
    new THREE.RingGeometry(
      radius - halfRoad - curbWidth,
      radius - halfRoad,
      segments,
      1,
      0,
      Math.PI / 2
    ),
    new THREE.MeshStandardMaterial({ map: curbTexture, roughness: 0.9 })
  );
  const curbOuter = new THREE.Mesh(
    new THREE.RingGeometry(
      radius + halfRoad,
      radius + halfRoad + curbWidth,
      segments,
      1,
      0,
      Math.PI / 2
    ),
    new THREE.MeshStandardMaterial({ map: curbTexture.clone(), roughness: 0.9 })
  );
  curbInner.position.y = 0.002;
  curbOuter.position.y = 0.002;

  // Repère "natif" (avant la rotation.y standard posée par l'appelant, voir
  // `createTileGroup`/`tileRotationY`) : arc centré en (-radius, radius),
  // tangent au bras ouest en (-radius, 0) et au bras sud en (0, radius) —
  // calcul détaillé et vérifié empiriquement (scan pixel par pixel) en session.
  const arc = new THREE.Group();
  arc.add(asphalt, curbInner, curbOuter);
  arc.rotation.x = -Math.PI / 2;
  arc.position.set(-radius, 0.01, radius);

  const wrapper = new THREE.Group();
  wrapper.add(arc);
  return wrapper;
}

export interface ProceduralStraightOptions {
  /** Largeur de la chaussée carrossable (fraction d'une case). */
  roadWidth?: number;
  /** Largeur de la bordure de chaque côté de la chaussée. */
  curbWidth?: number;
  /** Couleur unie de la bordure — `null` pour la fondre dans la chaussée
   * (ligne droite "plate", sans marquage particulier). */
  curbColor?: number | null;
  /** Couleur de la chaussée — bitume par défaut, terre pour une case peinte. */
  baseColor?: number;
  /** Couleur vers laquelle fondre en début/fin de case (voir
   * `createSurfaceFadeTexture`) — `null`/omis pour une chaussée unie sur toute
   * la longueur (case sans voisin de revêtement différent de ce côté). */
  fadeToStart?: number | null;
  fadeToEnd?: number | null;
}

/**
 * Ligne droite générée par calcul, même principe que `buildProceduralCorner` :
 * chaussée + bordures dimensionnées avec les MÊMES `roadWidth`/`curbWidth` que
 * le virage, pour un raccord sans décrochement radial à la jonction. Repère
 * natif en longueur sur Z (une case = 1 unité) — même convention que l'ancien
 * modèle Kenney `roadStraight.glb` qu'elle remplace, donc l'appelant continue
 * d'appliquer la même rotation d'instanciation (`STRAIGHT_EXTRA_ROTATION`,
 * voir `circuit3dTrackScene.ts`) sans changement.
 */
export function buildProceduralStraight(opts: ProceduralStraightOptions = {}): THREE.Object3D {
  const {
    roadWidth = PROCEDURAL_ROAD_WIDTH,
    curbWidth = PROCEDURAL_CURB_WIDTH,
    curbColor = null,
    baseColor = PROCEDURAL_ASPHALT_COLOR,
    fadeToStart = null,
    fadeToEnd = null,
  } = opts;
  const halfRoad = roadWidth / 2;
  const cellLength = 1;

  const hasFade = fadeToStart !== null || fadeToEnd !== null;
  const asphalt = new THREE.Mesh(
    new THREE.PlaneGeometry(roadWidth, cellLength),
    hasFade
      ? new THREE.MeshStandardMaterial({
          map: createSurfaceFadeTexture(baseColor, fadeToStart, fadeToEnd),
          roughness: 0.95,
        })
      : new THREE.MeshStandardMaterial({ color: baseColor, roughness: 0.95 })
  );
  asphalt.rotation.x = -Math.PI / 2;
  asphalt.position.y = 0.01;

  const group = new THREE.Group();
  group.add(asphalt);

  // Bordure fondue en même temps que la chaussée quand elle n'a pas de couleur
  // dédiée (`curbColor` null, cas des cases terre/bitume) — sans ça la chaussée
  // centrale glissait doucement d'une couleur à l'autre pendant que la bordure
  // sautait net à la jonction, deux transitions désynchronisées qui lisaient
  // comme un double fondu (repéré en testant sur une vraie jonction terre/bitume).
  const curbGeometry = new THREE.PlaneGeometry(curbWidth, cellLength);
  const curbMat =
    hasFade && curbColor === null
      ? new THREE.MeshStandardMaterial({
          map: createSurfaceFadeTexture(baseColor, fadeToStart, fadeToEnd),
          roughness: 0.9,
        })
      : new THREE.MeshStandardMaterial({
          color: curbColor ?? baseColor,
          roughness: 0.9,
        });
  const curbLeft = new THREE.Mesh(curbGeometry, curbMat);
  curbLeft.rotation.x = -Math.PI / 2;
  curbLeft.position.set(-(halfRoad + curbWidth / 2), 0.012, 0);
  const curbRight = new THREE.Mesh(curbGeometry, curbMat.clone());
  curbRight.rotation.x = -Math.PI / 2;
  curbRight.position.set(halfRoad + curbWidth / 2, 0.012, 0);
  group.add(curbLeft, curbRight);

  return group;
}
